/**
 * Нормализация названий гильдий.
 *
 * Одна реализация на клиент и сервер: при создании и при поиске дублей
 * имя обязано сводиться к одному и тому же виду, иначе «Рассвет» и
 * «Рaссвет» (с латинской «a») окажутся разными гильдиями.
 *
 * Источник: docs/01-функции/01-первый-запуск/реализация.md
 */
/**
 * Кириллические буквы, неотличимые от латинских.
 * Подмена — типовой способ обойти фильтр и выдать себя за чужую гильдию.
 */
const LOOKALIKE = {
    а: 'a',
    е: 'e',
    о: 'o',
    р: 'p',
    с: 'c',
    у: 'y',
    х: 'x',
    А: 'a',
    Е: 'e',
    О: 'o',
    Р: 'p',
    С: 'c',
    У: 'y',
    Х: 'x',
};
/**
 * Приводит название к виду для сравнения.
 *
 * Показываем игроку оригинал, ищем дубли по нормализованному виду.
 */
export function normalizeName(raw) {
    return Array.from(raw.trim().toLowerCase())
        .map((ch) => LOOKALIKE[ch] ?? ch)
        .join('');
}
export const NAME_MIN_LENGTH = 3;
export const NAME_MAX_LENGTH = 9;
/**
 * Проверяет название.
 *
 * Длина считается по кодовым точкам, а не по `.length`: эмодзи и
 * составные символы занимают две единицы UTF-16, и «🔥🔥» прошло бы
 * как четыре символа.
 *
 * Клиент вызывает это ради быстрой подсказки, но решение принимает
 * только сервер — клиентскую проверку легко обойти через DevTools.
 */
export function validateName(raw, forbiddenWords = []) {
    const trimmed = raw.trim();
    const length = Array.from(trimmed).length;
    if (length < NAME_MIN_LENGTH)
        return 'too_short';
    if (length > NAME_MAX_LENGTH)
        return 'too_long';
    const normalized = normalizeName(trimmed);
    for (const entry of forbiddenWords) {
        const word = normalizeName(entry.word);
        if (entry.match_type === 'exact' ? normalized === word : normalized.includes(word)) {
            return 'forbidden';
        }
    }
    return null;
}
/** Диапазон дискриминаторов: 0001–9999, как в документах. */
export const DISCRIMINATOR_MIN = 1;
export const DISCRIMINATOR_MAX = 9999;
/**
 * Номер, который ввёл игрок: ровно четыре ASCII-цифры, 0001–9999.
 *
 * Строго четыре знака, без дополнения нулями: «1» и «0001» в базе были бы
 * разными строками, а игрок видел бы одно и то же.
 */
export function isValidDiscriminator(value) {
    if (!/^[0-9]{4}$/.test(value))
        return false;
    const n = Number(value);
    return n >= DISCRIMINATOR_MIN && n <= DISCRIMINATOR_MAX;
}
/**
 * Выбирает свободный дискриминатор для имени.
 *
 * Случайный, а не следующий по порядку: последовательные номера выдали бы
 * порядок регистрации и позволили бы считать, сколько гильдий с таким именем.
 *
 * Источник случайности передаётся параметром — иначе выбор нельзя
 * прогнать в тесте детерминированно.
 */
export function pickDiscriminator(taken, random = Math.random) {
    const total = DISCRIMINATOR_MAX - DISCRIMINATOR_MIN + 1;
    if (taken.size >= total)
        return null;
    // Пробуем наугад: пока занято мало, попадание почти мгновенное.
    for (let attempt = 0; attempt < 24; attempt += 1) {
        const value = DISCRIMINATOR_MIN + Math.floor(random() * total);
        const candidate = String(value).padStart(4, '0');
        if (!taken.has(candidate))
            return candidate;
    }
    // Имя почти исчерпано — идём по порядку от случайной точки,
    // чтобы не выдать первый свободный номер и не зациклиться.
    const start = Math.floor(random() * total);
    for (let offset = 0; offset < total; offset += 1) {
        const value = DISCRIMINATOR_MIN + ((start + offset) % total);
        const candidate = String(value).padStart(4, '0');
        if (!taken.has(candidate))
            return candidate;
    }
    return null;
}
//# sourceMappingURL=guild-name.js.map